import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "react-hot-toast";

interface Course {
  _id: string;
  title: string;
  level?: string;
  modules: Array<{
    _id: string;
    title: string;
    contents: Array<{
      _id: string;
      title: string;
      type: string;
    }>;
  }>;
}

const TeacherLiveSessions: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [duration, setDuration] = useState<number>(60);
  const [timezone, setTimezone] = useState<string>("Africa/Nairobi");
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [recurrenceType, setRecurrenceType] = useState<string>("weekly");
  const [recurrenceInterval, setRecurrenceInterval] = useState<number>(1);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState<string>("");
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>([]);
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  // Fetch teacher's courses
  const { data: coursesResponse, isLoading: coursesLoading } = useQuery({
    queryKey: ["teacher-courses"],
    queryFn: async () => {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/elearning/courses`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    },
  });

  const courses = coursesResponse?.courses || [];

  // Fetch live sessions for selected course
  const { data: liveSessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["course-live-sessions", selectedCourse],
    queryFn: async () => {
      if (!selectedCourse) return [];
      const response = await axios.get(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/api/elearning/courses/${selectedCourse}/live-sessions`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data.data;
    },
    enabled: !!selectedCourse,
  });

  // Schedule mutation
  const scheduleMutation = useMutation({
    mutationFn: async (sessionData: any) => {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/elearning/live-sessions`,
        sessionData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Live session scheduled successfully!");
      queryClient.invalidateQueries({ queryKey: ["course-live-sessions"] });
      setShowScheduleForm(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to schedule session"
      );
    },
  });

  // Cancel mutation
  const cancelMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      await axios.delete(
        `${
          import.meta.env.VITE_BACKEND_URL
        }/api/elearning/live-sessions/${sessionId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
    },
    onSuccess: () => {
      toast.success("Live session cancelled successfully!");
      queryClient.invalidateQueries({ queryKey: ["course-live-sessions"] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to cancel session");
    },
  });

  const resetForm = () => {
    setSelectedModule("");
    setStartDate("");
    setStartTime("");
    setDuration(60);
    setTimezone("Africa/Nairobi");
    setIsRecurring(false);
    setRecurrenceType("weekly");
    setRecurrenceInterval(1);
    setRecurrenceEndDate("");
    setDaysOfWeek([]);
    setDayOfMonth(1);
  };

  // Function to determine session status based on time
  const getSessionStatus = (session: any) => {
    const now = new Date();
    const startTime = new Date(session.startAt);
    const endTime = new Date(session.endAt);

    if (session.status === "cancelled") return "cancelled";
    if (session.status === "completed") return "completed";
    if (now > endTime) return "ended";
    if (now >= startTime && now <= endTime) return "live";
    return "scheduled";
  };

  const handleSchedule = () => {
    if (!selectedCourse || !startDate || !startTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (isRecurring && !recurrenceEndDate) {
      toast.error("Please set an end date for recurring sessions");
      return;
    }

    if (isRecurring && recurrenceType === "weekly" && daysOfWeek.length === 0) {
      toast.error(
        "Please select at least one day of the week for weekly recurring sessions"
      );
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(startDateTime.getTime() + duration * 60000);

    const sessionData: any = {
      courseId: selectedCourse,
      startAt: startDateTime.toISOString(),
      endAt: endDateTime.toISOString(),
      timezone,
    };

    if (selectedModule) {
      sessionData.moduleId = selectedModule;
    }

    // Add recurring data if enabled
    if (isRecurring) {
      sessionData.isRecurring = true;
      sessionData.recurrencePattern = {
        type: recurrenceType,
        interval: recurrenceInterval,
        endDate: recurrenceEndDate
          ? new Date(recurrenceEndDate).toISOString()
          : null,
      };

      if (recurrenceType === "weekly" && daysOfWeek.length > 0) {
        sessionData.recurrencePattern.daysOfWeek = daysOfWeek;
      }

      if (recurrenceType === "monthly") {
        sessionData.recurrencePattern.dayOfMonth = dayOfMonth;
      }
    }

    scheduleMutation.mutate(sessionData);
  };

  const handleCancel = (sessionId: string) => {
    if (window.confirm("Are you sure you want to cancel this live session?")) {
      cancelMutation.mutate(sessionId);
    }
  };

  const selectedCourseData = courses?.find
    ? courses.find((c: Course) => c._id === selectedCourse)
    : null;
  const selectedModuleData = selectedCourseData?.modules.find(
    (m: any) => m._id === selectedModule
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-green-100 text-green-800";
      case "live":
        return "bg-red-100 text-red-800";
      case "ended":
        return "bg-gray-100 text-gray-800";
      case "completed":
        return "bg-gray-100 text-gray-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (coursesLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Live Sessions Management
        </h1>
        <p className="text-gray-600">
          Schedule and manage live classes for your courses using Google Meet.
        </p>
      </div>

      {/* Course Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Course
        </label>
        <select
          value={selectedCourse}
          onChange={(e) => {
            setSelectedCourse(e.target.value);
            setSelectedModule("");
          }}
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Choose a course...</option>
          {courses.map((course: Course) => (
            <option key={course._id} value={course._id}>
              {course.title}({course.level})
            </option>
          ))}
        </select>
      </div>

      {selectedCourse && (
        <>
          {/* Schedule Button */}
          <div className="mb-6">
            <button
              onClick={() => setShowScheduleForm(!showScheduleForm)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showScheduleForm ? "Cancel" : "Schedule New Session"}
            </button>
          </div>

          {/* Schedule Form */}
          {showScheduleForm && (
            <div className="bg-white rounded-lg shadow-md p-6 mb-8">
              <h2 className="text-xl font-semibold mb-4">
                Schedule Live Session
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Module Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Module (Optional)
                  </label>
                  <select
                    value={selectedModule}
                    onChange={(e) => {
                      setSelectedModule(e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a module...</option>
                    {selectedCourseData?.modules.map((module: any) => (
                      <option key={module._id} value={module._id}>
                        {module.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (minutes)
                  </label>
                  <select
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={90}>1.5 hours</option>
                    <option value={120}>2 hours</option>
                  </select>
                </div>
              </div>

              {/* Recurring Options */}
              <div className="mt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Make this a recurring session
                  </span>
                </label>
              </div>

              {isRecurring && (
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Recurrence Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Repeat
                    </label>
                    <select
                      value={recurrenceType}
                      onChange={(e) => setRecurrenceType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  {/* Recurrence Interval */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Every
                    </label>
                    <div className="flex items-center">
                      <input
                        type="number"
                        min="1"
                        max="30"
                        value={recurrenceInterval}
                        onChange={(e) =>
                          setRecurrenceInterval(Number(e.target.value))
                        }
                        className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        {recurrenceType === "daily"
                          ? "day(s)"
                          : recurrenceType === "weekly"
                          ? "week(s)"
                          : "month(s)"}
                      </span>
                    </div>
                  </div>

                  {/* End Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={recurrenceEndDate}
                      onChange={(e) => setRecurrenceEndDate(e.target.value)}
                      min={startDate || new Date().toISOString().split("T")[0]}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Days of Week (for weekly) */}
                  {recurrenceType === "weekly" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Days of Week
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          "monday",
                          "tuesday",
                          "wednesday",
                          "thursday",
                          "friday",
                          "saturday",
                          "sunday",
                        ].map((day) => (
                          <label key={day} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={daysOfWeek.includes(day)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setDaysOfWeek([...daysOfWeek, day]);
                                } else {
                                  setDaysOfWeek(
                                    daysOfWeek.filter((d) => d !== day)
                                  );
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-1 text-xs capitalize">
                              {day.slice(0, 3)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Day of Month (for monthly) */}
                  {recurrenceType === "monthly" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Day of Month
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={dayOfMonth}
                        onChange={(e) => setDayOfMonth(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Preview */}
              {selectedModuleData && startDate && startTime && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <h3 className="font-medium mb-2">Session Preview:</h3>
                  <p>
                    <strong>Course:</strong> {selectedCourseData?.title}
                  </p>
                  <p>
                    <strong>Module:</strong> {selectedModuleData?.title}
                  </p>
                  <p>
                    <strong>Start:</strong>{" "}
                    {format(
                      new Date(`${startDate}T${startTime}`),
                      "PPP 'at' p"
                    )}{" "}
                    (East African Time - Nairobi)
                  </p>
                  <p>
                    <strong>Duration:</strong> {duration} minutes
                  </p>
                </div>
              )}

              <div className="mt-6 flex gap-4">
                <button
                  onClick={handleSchedule}
                  disabled={scheduleMutation.isPending}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {scheduleMutation.isPending
                    ? "Scheduling..."
                    : "Schedule Session"}
                </button>
                <button
                  onClick={() => {
                    setShowScheduleForm(false);
                    resetForm();
                  }}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Existing Sessions */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Scheduled Sessions</h2>
            </div>

            {sessionsLoading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : liveSessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No live sessions scheduled for this course yet.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {liveSessions.map((session: any) => (
                  <div key={session._id} className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <h3 className="text-lg font-medium text-gray-900">
                            {session.courseId.title}
                          </h3>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                              getSessionStatus(session)
                            )}`}
                          >
                            {getSessionStatus(session)}
                          </span>
                          {session.isRecurring && (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                              Recurring
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <strong>Start:</strong>{" "}
                            {format(new Date(session.startAt), "PPP 'at' p")}
                          </div>
                          <div>
                            <strong>Duration:</strong>{" "}
                            {Math.round(
                              (new Date(session.endAt).getTime() -
                                new Date(session.startAt).getTime()) /
                                (1000 * 60)
                            )}{" "}
                            minutes
                          </div>
                          <div>
                            <strong>Timezone:</strong> East African Time -
                            Nairobi
                          </div>
                        </div>

                        {session.meetLink && (
                          <div className="mt-2">
                            <a
                              href={session.meetLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              View in Google Calendar →
                            </a>
                          </div>
                        )}
                      </div>

                      {getSessionStatus(session) === "scheduled" && (
                        <div className="ml-4">
                          <button
                            onClick={() => handleCancel(session._id)}
                            disabled={cancelMutation.isPending}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                          >
                            Cancel Session
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TeacherLiveSessions;
